import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Uppy, { UploadResult as UppyUploadResult } from '@uppy/core';
import { DashboardModal } from '@uppy/react';
import { useField } from 'formik';
import localforage from 'localforage';
import { ReactNode, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { notUndefined } from '~/helpers/filter';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useUppy } from '~/hooks/useUppy';
import type { Kind } from '.';
import { UploadPreviews } from './UploadPreviews';
import { Body, Meta } from './uppy';

// uppy css
import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';

// Icons
import { faPaperclip } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { UploadImageIcon } from '~/icons/UploadImageIcon';

//UI
import { Label } from '../ui/Label';
import { Pill } from '../ui/nucleus/Pill';

type Props = {
  id: string;
  children?: ReactNode;
  imagesOnly?: boolean;
  isMainUpload?: boolean;
  usePillStyle?: boolean;
  buttonText?: string;
  className?: string;
  disabled?: boolean;
  onUploaded: (uploaded: UploadResult[]) => void | Promise<void>;
  onUploadComplete?: (closeDialog: () => void) => void;
};

type UploadCompleteCallback = (
  result: UppyUploadResult<Meta, Body>
) => Promise<void>;

export type UploadResult = {
  id: string;
  preview: string;
  name?: string;
  kind: Kind;
};

export function Upload({
  children,
  id,
  imagesOnly,
  onUploaded,
  isMainUpload = false,
  usePillStyle = false,
  buttonText,
  className,
  disabled = false,
  onUploadComplete,
}: Props) {
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation();
  const { uppy, open, setOpen } = useUppy(imagesOnly ? 'image' : 'file');

  const handleComplete: UploadCompleteCallback = useCallback(
    async (result): Promise<void> => {
      try {
        const results = result.successful?.map((file) => {
          if (!file.response?.body) {
            throw new Error('Something went wrong');
          }

          return {
            kind: file.response.body.kind,
            id: file.response.body.id,
            preview: file.preview ?? '',
            name: file.name,
          };
        });

        // Keep a copy of the uploaded images in offline storage so that it can continue to be used until the form is submitted... avoids having to retrieve the signed URL for unsaved uploads
        const blobs = result.successful
          ?.map((file) => {
            if (file.response?.body) {
              const id = file.response.body.id;

              return { id, data: file.data };
            }
          })
          .filter(notUndefined);

        if (blobs) {
          await addImageUploadResultsToStorage(blobs);
        }

        // Call onUploaded - don't await it so modal can close immediately
        // Any async operations (like compression/AI analysis) will run in the background
        onUploaded(results ?? []);
      } catch (error) {
        // Log or surface any caught errors
        console.error('Error during upload completion:', error);
        // Re-throw to allow caller to handle if needed, but finally will still run
        throw error;
      } finally {
        // Ensure onUploadComplete is invoked (or call setOpen(false) directly if onUploadComplete is absent) so the modal always closes
        if (onUploadComplete) {
          onUploadComplete(() => setOpen(false));
        } else {
          setOpen(false);
        }
      }
    },
    [onUploaded, onUploadComplete, setOpen]
  );

  useEffect(() => {
    if (open) {
      uppy.on('complete', handleComplete);

      return () => {
        uppy.off('complete', handleComplete);
      };
    }
  }, [open, uppy, handleComplete]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpenModal = () => {
    // Prevent opening modal when disabled
    if (disabled) {
      return;
    }
    // Cancel all current uploads
    uppy.cancelAll();
    // Remove all files from Uppy's internal state
    uppy.getFiles().forEach((file) => {
      uppy.removeFile(file.id);
    });
    setOpen(true);
  };

  return (
    <>
      {(() => {
        const showFullUploader = isMainUpload && !usePillStyle;

        let buttonContent: ReactNode;
        if (showFullUploader) {
          buttonContent = (
            <UploadImageIcon className='h-12 w-12 text-primary-container' />
          );
        } else if (usePillStyle) {
          buttonContent = (
            <Pill className='ml-0 flex items-center'>
              <UploadImageIcon className='h-5 w-5 text-primary' />
              {buttonText ?? (imagesOnly ? t('Upload Images') : t('Upload'))}
            </Pill>
          );
        } else if (children) {
          buttonContent = children;
        } else if (imagesOnly) {
          buttonContent = <UploadImageIcon className='h-6 w-6' />;
        } else {
          buttonContent = (
            <Pill className='ml-0 flex items-center'>
              <FontAwesomeIcon icon={faPaperclip} className='mr-2' />
              {buttonText ?? t('Upload')}
            </Pill>
          );
        }

        return (
          <button
            type='button'
            onClick={handleOpenModal}
            disabled={disabled}
            className={classNames(
              showFullUploader
                ? 'flex w-full items-center justify-center rounded-md border-2 border-dashed border-outline-variant p-5'
                : className,
              {
                'cursor-not-allowed opacity-50': disabled,
                'pointer-events-none': disabled,
              }
            )}
            aria-label={
              showFullUploader
                ? t('Upload new file')
                : buttonText ?? t('Upload')
            }
            aria-disabled={disabled}
          >
            {buttonContent}
          </button>
        );
      })()}
      {open && id && (
        <DashboardModal
          id={id}
          uppy={uppy}
          open={open}
          theme='light'
          onRequestClose={handleClose}
          proudlyDisplayPoweredByUppy={false}
          note='Attachment limit 50 MB'
          style={{
            zIndex: 10000,
          }}
          locale={{
            strings: {
              browseFiles: isMobile ? 'Add files/photos' : 'browse files',
              dropPasteFiles: isMobile
                ? '%{browseFiles}'
                : 'Drop files here or %{browseFiles}',
            },
          }}
        />
      )}
    </>
  );
}

type UploadFieldProps = {
  name: string;
  isMainUpload?: boolean;
} & Omit<Props, 'onUploaded'>;

export function UploadField(props: UploadFieldProps) {
  const { t } = useTranslation();
  const [field, meta, helpers] = useField<UploadResult[]>(props.name);

  // Determine if pill style should be used
  const usePillStyle =
    !props.isMainUpload || (field.value && field.value.length > 0);

  // Determine button text based on imagesOnly
  const buttonText = props.imagesOnly ? t('Add Image') : t('Add File');

  // Safe remove handler with error boundary
  const handleRemove = useCallback(
    (id: string) => {
      try {
        helpers.setValue(field.value.filter((item) => item.id !== id));
      } catch (error) {
        console.error('Error removing upload:', error);
        // Try to force-set an empty array if filtering fails
        try {
          helpers.setValue([]);
        } catch (innerError) {
          console.error('Error clearing uploads:', innerError);
        }
      }
    },
    [field.value, helpers]
  );

  return (
    <>
      {props.isMainUpload && (
        <div className='mb-1'>
          <Label>{props.imagesOnly ? 'Images' : 'Files'}</Label>
        </div>
      )}
      {field.value && field.value.length > 0 && (
        <UploadPreviews uploads={field.value} onRemove={handleRemove} />
      )}
      <Upload
        {...props}
        isMainUpload={props.isMainUpload}
        usePillStyle={usePillStyle}
        buttonText={buttonText}
        onUploaded={(uploaded) => {
          helpers.setValue(field.value.concat(uploaded));
          helpers.setTouched(true, false);
        }}
      />
    </>
  );
}

type ControlledUploadProps = {
  value: UploadResult[];
  isPill?: boolean;
  onChange: (value: UploadResult[]) => void;
} & Omit<Props, 'onUploaded'>;

// ---- CONTROLLED UPLOAD ----
export function ControlledUpload({
  id,
  value,
  onChange,
  isPill = false,
}: ControlledUploadProps) {
  const hasUploads = value.length > 0;

  // Safe remove handler with error boundary
  const handleRemove = useCallback(
    (removeId: string) => {
      try {
        onChange(value.filter((item) => item.id !== removeId));
      } catch (error) {
        console.error('Error removing upload:', error);
        // Try to force-set an empty array if filtering fails
        try {
          onChange([]);
        } catch (innerError) {
          console.error('Error clearing uploads:', innerError);
        }
      }
    },
    [value, onChange]
  );

  return (
    <>
      {hasUploads && <UploadPreviews uploads={value} onRemove={handleRemove} />}
      <Upload
        id={id}
        onUploaded={(uploaded) => onChange([...value, ...uploaded])}
        isPill={isPill}
      />
    </>
  );
}

// ------------------------------

type OfflineImages = {
  images: {
    id: string;
    data: Blob;
    expiresAt: number;
  }[];
};

const addImageUploadResultsToStorage = async (
  images: { id: string; data: Blob }[]
) => {
  try {
    const key = 'all-upload-previews';
    const offline = await localforage.getItem<OfflineImages>(key);
    const expires = Date.now() + 24 * 60 * 60 * 1000;

    await localforage.setItem(key, {
      images: [
        ...(offline?.images.filter((image) => image.expiresAt < Date.now()) ??
          []),
        ...images.map((image) => ({ ...image, expiresAt: expires })),
      ],
    });
  } catch (err) {
    // Log error instead of failing silently for better debugging
    console.error('Failed to save upload previews to localforage:', err);
  }
};

export const loadPreviewsFromStorage = async (): Promise<
  OfflineImages['images']
> => {
  const key = 'all-upload-previews';
  const offline = await localforage.getItem<OfflineImages>(key);

  if (!(offline && offline.images)) {
    return [];
  }

  return offline.images;
};

// Helper function to remove a file from Uppy instance by various ID types
const removeUppyFileById = (
  uppyInstance: Uppy<Meta, Body>,
  fileIdToRemove: string | null
) => {
  if (!fileIdToRemove) {
    return;
  }

  // Try to get and remove the file directly by its Uppy ID
  const uppyFile = uppyInstance.getFile(fileIdToRemove);
  if (uppyFile) {
    uppyInstance.removeFile(uppyFile.id);
    return; // File found and removed
  }

  // Fallback: If not found by direct ID, search in all files
  // This handles cases where fileIdToRemove might be a backend ID or other identifier
  const fileToRemoveFromAll = uppyInstance.getFiles().find(
    (f) =>
      f.meta.backendId === fileIdToRemove || // Check against custom metadata
      (f.response?.body as Body)?.id === fileIdToRemove // Check against ID in response body
  );

  if (fileToRemoveFromAll) {
    uppyInstance.removeFile(fileToRemoveFromAll.id);
  }
};

// --- SINGLE UPLOAD FIELD ---
// Props for the new SingleUploadField component
type SingleUploadFieldProps = {
  name: string; // For Formik
  id: string; // For Uppy Dashboard modal
  children?: ReactNode;
  imagesOnly?: boolean;
  isMainUpload?: boolean;
  hasLabel?: boolean;
};

export function SingleUploadField({
  name,
  id,
  children,
  imagesOnly,
  isMainUpload,
  hasLabel = true,
}: SingleUploadFieldProps) {
  const [field, , helpers] = useField<UploadResult[]>(name);
  const { uppy } = useUppy(imagesOnly ? 'image' : 'file');
  const currentFileId =
    field.value && field.value.length > 0 ? field.value[0].id : null;
  const { t } = useTranslation();

  // Determine if pill style should be used
  const usePillStyle = !isMainUpload || (field.value && field.value.length > 0);

  // Determine button text based on whether a file exists and imagesOnly
  const hasImage = field.value?.length > 0;
  const buttonText = hasImage
    ? imagesOnly
      ? t('Replace Image')
      : t('Replace File')
    : imagesOnly
    ? t('Add Image')
    : t('Add File');

  // Safe remove handler with error boundary
  // This prevents crashes when removing broken/malformed images
  const handleRemove = useCallback(() => {
    try {
      if (currentFileId) {
        // Use the utility function to remove the file from Uppy
        removeUppyFileById(uppy, currentFileId);
      }
      helpers.setValue([]);
    } catch (error) {
      console.error('Error removing file:', error);
      // Even if Uppy removal fails, still try to clear the form state
      try {
        helpers.setValue([]);
      } catch (innerError) {
        console.error('Error clearing form state:', innerError);
      }
    }
  }, [currentFileId, uppy, helpers]);

  return (
    <>
      {hasLabel && <Label>{imagesOnly ? 'Image' : 'File'}</Label>}
      {field.value && field.value.length > 0 && (
        <UploadPreviews uploads={field.value} onRemove={handleRemove} />
      )}
      <div className='mt-2'>
        <Upload
          id={id}
          imagesOnly={imagesOnly}
          isMainUpload={isMainUpload}
          usePillStyle={usePillStyle}
          buttonText={buttonText}
          onUploaded={(newlyUploadedBatch: UploadResult[]) => {
            const oldFileId = currentFileId;

            if (newlyUploadedBatch.length > 0) {
              const newFile = newlyUploadedBatch[newlyUploadedBatch.length - 1];
              helpers.setValue([newFile]);
              // If there was an old file and it's different from the new one, remove old from Uppy
              if (oldFileId && oldFileId !== newFile.id) {
                // Use the utility function to remove the old file from Uppy
                removeUppyFileById(uppy, oldFileId);
              }
            } else {
              // Batch is empty (cancel/failure)
              helpers.setValue([]);
              // If there was an old file, remove it from Uppy
              if (oldFileId) {
                // Use the utility function to remove the old file from Uppy
                removeUppyFileById(uppy, oldFileId);
              }
            }
          }}
        >
          {children}
        </Upload>
      </div>
    </>
  );
}
